import {
  PackageDepartmentEnum,
  type ClientServiceProfileFragmentFragment,
} from "@/__generated__/graphql";
import { type PetProfileFormData } from "./service-profile-form/pet/usePetProfileForm";
import { getCapitalizeWord } from "@/lib/utils";
import { type ElderCareFormData } from "./service-profile-form/elder/useElderCareForm";
import {
  requiredAssistances,
  symptoms,
} from "./service-profile-form/elder/lib";
import { type BabySitterFormData } from "./service-profile-form/baby/useBabyProfileForm";
import { babysitterTasksOptions } from "./service-profile-form/baby/lib";
import { petConditionKeys } from "./service-profile-form/pet/lib";

export type ServiceProfileType = "pet" | "elder" | "baby";

export const ServiceProfileTypeMap: Record<ServiceProfileType, string> = {
  pet: "Pet Care",
  elder: "Eldercare",
  baby: "Child",
};

export const ServiceProfileServiceIdMap: Record<ServiceProfileType, string> = {
  pet: "21",
  elder: "33",
  baby: "17",
};

export type ServiceProfileItem = {
  id: string;
  name: string;
  visibleAttributes: string[];
  isDefault: boolean;
  type: ServiceProfileType;
};

const keysVisibleProfileAttributes = (type: ServiceProfileType) => {
  switch (type) {
    case "pet":
      return ["type", "breed", "age", "gender"];
    case "elder":
      return ["name", "age", "weight"];
    case "baby":
      return ["name", "age", "babysitter_tasks"];
  }
};

export const visibleAttributesValue = (
  attributes: unknown,
  type: ServiceProfileType,
): string[] => {
  if (typeof attributes !== "object" || attributes === null) {
    return [];
  }

  return keysVisibleProfileAttributes(type).reduce((acc, key) => {
    const value = (attributes as Record<string, unknown>)[key];

    // Handle babysitter_tasks object structure
    if (
      key === "babysitter_tasks" &&
      typeof value === "object" &&
      value !== null
    ) {
      const selectedTasks = Object.entries(value)
        .filter(([_, isSelected]) => isSelected === true)
        .map(([taskKey]) => {
          const task = babysitterTasksOptions.find(
            (opt) => opt.key === taskKey,
          );
          return task?.label || taskKey;
        })
        .slice(0, 2);

      if (selectedTasks.length > 0) {
        acc.push(selectedTasks.join(" • "));
      }
    } else if (Array.isArray(value)) {
      acc.push(value.slice(0, 2).join(" • "));
    } else if (typeof value === "string") {
      acc.push(getCapitalizeWord(value));
    }

    return acc;
  }, [] as string[]);
};

export const mapServiceProfileToItem = (
  profile: ClientServiceProfileFragmentFragment,
  type: ServiceProfileType,
): ServiceProfileItem => {
  return {
    id: profile.id,
    name: getCapitalizeWord(profile.name),
    visibleAttributes: visibleAttributesValue(
      { name: profile.name, ...profile.profileAttributes },
      type,
    ),
    isDefault: profile.primary,
    type,
  };
};

export const mapDepartmentToServiceProfileType = (
  department: PackageDepartmentEnum,
): ServiceProfileType => {
  switch (department) {
    case PackageDepartmentEnum.PetCare:
      return "pet";
    case PackageDepartmentEnum.ElderCare:
      return "elder";
    case PackageDepartmentEnum.Babysitter:
      return "baby";
    default:
      return "pet";
  }
};

export const inlineVisibleAttributesValue = (
  attributes: unknown,
  type: ServiceProfileType,
) => {
  return visibleAttributesValue(attributes, type).join(" • ");
};

export type ServiceProfileFormScreenProps = {
  pageTitle: string;
  children: React.ReactNode;
  actions: React.ReactNode;
};

export const stringArrayToFormArray = (
  values: string[],
  allOptions: string[],
): Array<{ value: string; selected: boolean }> => {
  return allOptions.map((option) => ({
    value: option,
    selected: values.includes(option),
  }));
};

export const formArrayToStringArray = (
  formArray: Array<{ value: string; selected: boolean }>,
): string[] => {
  return formArray.filter((item) => item.selected).map((item) => item.value);
};

// Helper to convert object structure to string array
export const objectToStringArray = (obj: Record<string, boolean>): string[] => {
  return Object.entries(obj)
    .filter(([_, value]) => value === true)
    .map(([key]) => key);
};

// Helper to convert string array to object structure
export const stringArrayToObject = (
  values: string[],
  allKeys: string[],
): Record<string, boolean> => {
  return allKeys.reduce(
    (acc, key) => {
      acc[key] = values.includes(key);
      return acc;
    },
    {} as Record<string, boolean>,
  );
};

export type EldercareAttributes = Omit<
  ElderCareFormData,
  "name" | "symptomps" | "required_assistances"
> & {
  symptomps: string[];
  required_assistances: string[];
};

export type BabySitterAttributes = Omit<
  BabySitterFormData,
  "name" | "babysitter_tasks"
> & {
  babysitter_tasks: string[];
};

export type PetProfileAttributes = Omit<
  PetProfileFormData,
  "name" | "special_conditions"
> & {
  special_conditions: string[];
};

export const transformServiceProfile = (
  type: ServiceProfileType,
  initialAttributes:
    | Omit<ElderCareFormData, "name">
    | Omit<BabySitterFormData, "name">
    | Omit<PetProfileFormData, "name">,
): EldercareAttributes | BabySitterAttributes | PetProfileAttributes => {
  if (type === "elder") {
    const { symptomps, required_assistances, ...rest } =
      initialAttributes as ElderCareFormData;
    return {
      ...rest,
      symptomps: formArrayToStringArray(symptomps ?? []),
      required_assistances: formArrayToStringArray(required_assistances),
    } as EldercareAttributes;
  } else if (type === "baby") {
    const { babysitter_tasks, ...rest } =
      initialAttributes as BabySitterFormData;
    return {
      ...rest,
      babysitter_tasks: objectToStringArray(babysitter_tasks ?? {}),
    } as BabySitterAttributes;
  } else {
    const { special_conditions, ...rest } =
      initialAttributes as PetProfileFormData;
    return {
      ...rest,
      special_conditions: formArrayToStringArray(special_conditions ?? []),
    } as PetProfileAttributes;
  }
};

export const buildServiceProfileAttributes = (
  type: ServiceProfileType,
  initialAttributes: Record<string, unknown>,
) => {
  const attributes = { ...initialAttributes };
  if (type === "elder") {
    const initialSymptomps = (attributes.symptomps as string[]) ?? [];
    const initialRequiredAssistances =
      (attributes.required_assistances as string[]) ?? [];
    attributes.symptomps = stringArrayToFormArray(initialSymptomps, symptoms);
    attributes.required_assistances = stringArrayToFormArray(
      initialRequiredAssistances,
      requiredAssistances,
    );
    return attributes as ElderCareFormData;
  } else if (type === "baby") {
    const initialBabysitterTasks =
      (attributes.babysitter_tasks as string[]) ?? [];
    const allTaskKeys = babysitterTasksOptions.map((opt) => opt.key);
    attributes.babysitter_tasks = stringArrayToObject(
      initialBabysitterTasks,
      allTaskKeys,
    );
    return attributes as BabySitterFormData;
  } else {
    const initialSpecialConditions =
      (attributes.special_conditions as string[]) ?? [];
    attributes.special_conditions = stringArrayToFormArray(
      initialSpecialConditions,
      petConditionKeys,
    );
    return attributes as PetProfileFormData;
  }
};

type ServiceProfileMap = {
  pet: PetProfileFormData;
  elder: ElderCareFormData;
  baby: BabySitterFormData;
};

export const transformServiceProfileToDetail = <T extends ServiceProfileType>(
  type: T,
  profile: ClientServiceProfileFragmentFragment,
): ServiceProfileMap[T] => {
  const attributes = buildServiceProfileAttributes(
    type,
    profile.profileAttributes as Record<string, unknown>,
  ) as ServiceProfileMap[T];

  return {
    id: profile.id,
    ...attributes,
    name: profile.name,
  } as ServiceProfileMap[T];
};
